import { useCallback, useEffect, useState } from 'react'
import clsx from 'clsx'
import type { Message } from '@bufbuild/protobuf'
import { useQuery as useConnectQuery } from '@connectrpc/connect-query'
import { Trans } from '@lingui/react/macro'
import { useMutation } from '@tanstack/react-query'

import { useGetConfig } from '@jotta/grpc-connect-client/config'
import { publicTransport } from '@jotta/grpc-connect-client/transport'
import { mutate } from '@jotta/grpc-connect-client/util'
import type { Country } from '@jotta/grpc-connect-openapi/country'
import { Feature } from '@jotta/grpc-connect-openapi/feature'
import { getCurrentCountry } from '@jotta/grpc-connect-openapi/geoQuery'
import type { LoggedIn as LoggedInMessage } from '@jotta/grpc-connect-openapi/login'
import { getLogin } from '@jotta/grpc-connect-openapi/loginQuery'
import type {
  SignupResponse,
  SignupResponse_EmailVerification,
} from '@jotta/grpc-connect-openapi/signup'
import type {
  BusinessSignupRequest,
  SignupRequest,
} from '@jotta/grpc-connect-openapi/signup'
import {
  BusinessSignupRequestSchema,
  SignupRequestSchema,
} from '@jotta/grpc-connect-openapi/signup'
import {
  businessSignup as signupBusinessDescriptor,
  signup as signupDescriptor,
} from '@jotta/grpc-connect-openapi/signupQuery'
import { useInteractionListener } from '@jotta/hooks/useInteractionListener'
import { useCurrentLanguage } from '@jotta/i18n'
import type { PartialMessage } from '@jotta/types/compat'
import { OrDivider } from '@jotta/ui/Divider'
import { ErrorMessage } from '@jotta/ui/ErrorMessage'
import { LoadingOverlay } from '@jotta/ui/LoadingOverlay'
import { ModalDialog } from '@jotta/ui/ModalDialog'
import SvgFillWarningCircle from '@jotta/ui/SvgFillWarningCircle'
import { useBrandCode } from '@jotta/ui/useBrandTheme'
import { autoLogin } from '@jotta/utils/auth'
import { exhaustiveGuard } from '@jotta/utils/exhaustive'

import { AccountVerificationModal } from './AccountVerificationModal'
import { AppPromotionDrawer } from './AppPromotionDrawer'
import { CreateBusinessUser } from './CreateBusinessUser'
import { CreatePreSignupUser } from './CreatePreSignupUser'
import { CreatePrivateUser } from './CreatePrivateUser'
import { CreateVoucherUser } from './CreateVoucherUser'
import { FederatedSignupConfirmation } from './FederatedSignupConfirmation'
import type { FederationIntent } from './Federation'
import { Federation } from './Federation'
import { persistInstallationCode } from './installationCodeStore'
import { generateSignupToken } from './signupForm'
import { useFederationStore } from './useFederationStore'

export type LoggedIn = Omit<LoggedInMessage, keyof Message>

export type UserType =
  | 'private'
  | 'business'
  | 'voucher'
  | {
      case: 'presignup'
      phoneNumber: string
    }

type SignupResult = {
  installationCode?: string
  redirectUrl?: string
  autoLogin?: LoggedIn
}

export type EmailVerification = Omit<
  SignupResponse_EmailVerification,
  keyof Message
>
export type CaptchaSolution = {
  solution: string
  timeMillis: number
}

export type SignupRequestData = {
  request: PartialMessage<SignupRequest>['request']
  voucherCode?: string
  customerGroupCode?: string
  wantsNewsletter: boolean
  referralCode?: string
}
export type SignupRequestCallback = (request: SignupRequestData) => Promise<{
  requestData: SignupRequestData
  response: SignupResponse
}>

const userAgent = navigator.userAgent
const isIos = /iPad|iPhone|iPod/.test(userAgent)
const isAndroid = /android/i.test(userAgent)

const usePromoteApp = (
  allow: boolean,
  loginToken: string | undefined,
): boolean => {
  const disallowed = !allow || !(isIos || isAndroid)

  const brandCode = useBrandCode()
  const { data } = useConnectQuery(
    getLogin,
    {
      loginToken,
      brandCode,
    },
    {
      enabled: Boolean(loginToken) && !disallowed,
    },
  )

  if (disallowed) {
    return false
  } else if (!loginToken) {
    return true
  }

  const result = data?.result
  switch (result?.case) {
    case undefined:
    case 'notFound':
      return false
    case 'login':
      return result.value.clientId === 'web'
    default:
      return exhaustiveGuard(result)
  }
}

export function CreateUser({
  type,
  allowAppPromotion,
  federationIntent,
  loginToken,
  className,
  keepRouteOnSuccess,
  countryOverride,
  customerGroupOverride,
  familyInviteId,
  prefill,
  ctaLabelText,
  onClose,
  onRequestLogin,
  referralCode,
  redirectUrl,
}: {
  type: UserType
  allowAppPromotion: boolean
  federationIntent?: FederationIntent
  loginToken?: string
  className?: string
  keepRouteOnSuccess?: boolean
  customerGroupOverride?: string
  countryOverride?: Country
  prefill?: { name?: string; email?: string; voucherCode?: string }
  familyInviteId?: string
  ctaLabelText?: string
  onClose?: () => void
  onRequestLogin?: () => void
  referralCode?: string
  redirectUrl?: string
}) {
  const { pendingFederation, setPendingFederation } = useFederationStore()
  const [signupResult, setSignupResult] = useState<SignupResult>()
  const [appPromotionDeclined, setAppPromotionDeclined] = useState(false)
  const promoteApp = usePromoteApp(
    allowAppPromotion && !appPromotionDeclined,
    loginToken,
  )

  const [enableCaptcha, setEnableCaptcha] = useState(false)
  const [captchaSolution, setCaptchaSolution] = useState<CaptchaSolution>()
  const [emailVerification, setEmailVerification] =
    useState<EmailVerification>()
  const [accountVerificationFailed, setAccountVerificationFailed] =
    useState(false)
  const { getInteractionState } = useInteractionListener()

  const language = useCurrentLanguage()
  const { data: getConfigResponse } = useGetConfig(customerGroupOverride)
  const brandingInfo = getConfigResponse?.brandingInfo
  const customerGroupCode =
    customerGroupOverride || brandingInfo?.customerGroupCode
  const supportsVoucher =
    !familyInviteId &&
    Boolean(getConfigResponse?.activeFeatures?.includes(Feature.VOUCHER_SIGNUP))

  const { data: currentCountry } = useConnectQuery(
    getCurrentCountry,
    {},
    {
      throwOnError: false,
      select: data => data.country,
    },
  )
  const country = countryOverride || currentCountry

  const {
    data: signupPrivateResponse,
    mutate: signupPrivate,
    mutateAsync: signupPrivateAsync,
    error: signupPrivateError,
    isPending: isPrivatePending,
  } = useMutation({
    throwOnError: false,
    mutationFn: async (requestData: SignupRequestData) => {
      const {
        request,
        wantsNewsletter,
        voucherCode,
        customerGroupCode: requestedCustomerGroupCode,
        referralCode,
      } = requestData
      const {
        durationMillis,
        mouseMoveCount,
        keydownCount,
        clickCount,
        focusCount,
        inputCount,
        timings,
      } = getInteractionState()
      const signupRequest = await generateSignupToken(
        SignupRequestSchema,
        {
          customerGroupCode: requestedCustomerGroupCode || customerGroupCode,
          language,
          country,
          voucherCode,
          familyInviteId,
          wantsNewsletter,
          request,
          loginToken,
          origin: location.origin,
          generateInstallationCode: promoteApp,
          referralCode: referralCode,
          captcha: captchaSolution?.solution,
          meta: {
            d: `${durationMillis}`,
            m: `${mouseMoveCount}`,
            k: `${keydownCount}`,
            c: `${clickCount}`,
            f: `${focusCount}`,
            i: `${inputCount}`,
            t: `${timings.join(',')}`,
            ct: `${captchaSolution?.timeMillis || ''}`,
          },
        },
        (message, token) => (message.token = token),
      )

      const response = await mutate(
        signupDescriptor,
        signupRequest,
        publicTransport,
      )

      return {
        requestData,
        request: signupRequest,
        response,
      }
    },
  })

  // re-submit signup request with captcha solution
  useEffect(() => {
    if (
      captchaSolution &&
      signupPrivateResponse &&
      signupPrivateResponse.request.captcha !== captchaSolution.solution
    ) {
      signupPrivate(signupPrivateResponse.requestData)
    }
  }, [captchaSolution, signupPrivate, signupPrivateResponse])

  const {
    mutateAsync: signupBusiness,
    data: signupBusinessResponse,
    error: signupBusinessError,
    isPending: isBusinessPending,
  } = useMutation({
    throwOnError: false,
    mutationFn: async (req: PartialMessage<BusinessSignupRequest>) =>
      mutate(
        signupBusinessDescriptor,
        await generateSignupToken(
          BusinessSignupRequestSchema,
          {
            customerGroupCode,
            language,
            country,
            ...req,
          },
          (req, token) => (req.token = token),
        ),
        publicTransport,
      ),
  })

  const redirectTo = keepRouteOnSuccess
    ? location.href
    : redirectUrl
      ? redirectUrl
      : `${location.origin}/web/sync`

  const onSuccess = useCallback(
    (loggedIn: LoggedIn) =>
      setSignupResult({
        autoLogin: loggedIn,
      }),
    [setSignupResult],
  )

  useEffect((): undefined => {
    if (signupPrivateResponse) {
      const signupPrivateResult = signupPrivateResponse.response.result
      switch (signupPrivateResult?.case) {
        case 'success': {
          const success = signupPrivateResult.value
          setSignupResult({
            autoLogin: success,
            installationCode: success.installationCode,
          })
          break
        }

        case 'redirect': {
          const redirect = signupPrivateResult.value
          setSignupResult({
            redirectUrl: redirect.url,
            installationCode: redirect.installationCode,
          })
          break
        }
        case 'captcha': {
          setEnableCaptcha(true)
          break
        }
        case 'emailVerification': {
          setEmailVerification(signupPrivateResult.value)
          break
        }
        case undefined:
        case 'emailInUse':
        case 'signupNotSupported':
          throw new Error('signup failed')
        default:
          return exhaustiveGuard(signupPrivateResult)
      }
    }
  }, [setSignupResult, signupPrivateResponse])

  useEffect((): undefined => {
    if (signupBusinessResponse) {
      const signupBusinessResult = signupBusinessResponse.result
      switch (signupBusinessResult?.case) {
        case 'success':
          onSuccess(signupBusinessResult.value)
          break
        case 'redirect':
          window.location.assign(signupBusinessResult.value.url)
          break
        case undefined:
        case 'emailInUse':
        case 'signupNotSupported':
          throw new Error('signup failed')
        default:
          return exhaustiveGuard(signupBusinessResult)
      }
    }
  }, [onSuccess, signupBusinessResponse])

  const downloadMobileAppLink =
    !signupResult || !promoteApp
      ? undefined
      : isIos
        ? brandingInfo?.downloadIosLink
        : isAndroid
          ? brandingInfo?.downloadAndroidLink
          : undefined

  const actionableSignupResult = !downloadMobileAppLink
    ? signupResult
    : undefined

  // After a successful signup, we receive the means to log the user in either by following a redirect to complete an OIDC flow or direct auto-login.
  // This should happen automatically unless we are promoting the mobile apps.
  useEffect(() => {
    if (actionableSignupResult) {
      const autoLoginLogin = actionableSignupResult.autoLogin
      const redirectUrl = actionableSignupResult.redirectUrl
      if (autoLoginLogin) {
        autoLogin({
          username: autoLoginLogin.username,
          token: autoLoginLogin.autoLoginCode,
          redirectTo: redirectTo,
        })
      } else if (redirectUrl) {
        window.location.assign(redirectUrl)
      } else {
        throw new Error('no action found for successful signup')
      }
    }
  }, [actionableSignupResult, redirectTo])

  if (!customerGroupCode || actionableSignupResult) {
    return <LoadingOverlay id="CreateUser" open />
  }

  const ctaClassName = 'self-center'

  const baseLayoutClass = 'flex flex-col gap-4 w-full'
  return (
    <div className={clsx(baseLayoutClass, className)}>
      {type === 'private' &&
        federationIntent &&
        getConfigResponse?.viewConfig?.showLinkedAccounts && (
          <>
            <Federation intent={federationIntent} />
            <OrDivider />
          </>
        )}

      {accountVerificationFailed && (
        <div className="alert alert-danger flex items-center gap-2 text-danger">
          <SvgFillWarningCircle height={24} />
          <Trans>Account verification failed</Trans>
        </div>
      )}

      {(() => {
        if (typeof type !== 'string') {
          return (
            <CreatePreSignupUser
              phoneNumber={type.phoneNumber}
              customerGroupCode={customerGroupCode}
              className={baseLayoutClass}
              isPending={isPrivatePending}
              signup={signupPrivateAsync}
            />
          )
        }

        switch (type) {
          case 'voucher':
            return (
              <CreateVoucherUser
                customerGroupCode={customerGroupCode}
                className={baseLayoutClass}
                onRequestLogin={onRequestLogin}
                isPending={isPrivatePending}
                signup={signupPrivateAsync}
                prefill={prefill}
              />
            )
          case 'private':
            return (
              <CreatePrivateUser
                className={baseLayoutClass}
                ctaClassName={ctaClassName}
                ctaLabelText={ctaLabelText}
                onRequestLogin={onRequestLogin}
                isPending={isPrivatePending}
                supportsVoucher={supportsVoucher}
                signup={signupPrivateAsync}
                prefill={{
                  ...prefill,
                  customerGroupCode,
                }}
                referralCode={referralCode}
              />
            )
          case 'business':
            return (
              <CreateBusinessUser
                className={baseLayoutClass}
                ctaClassName={ctaClassName}
                ctaLabelText={ctaLabelText}
                isPending={isBusinessPending}
                supportsVoucher={supportsVoucher}
                signup={signupBusiness}
                prefill={{
                  ...prefill,
                  customerGroupCode,
                }}
                referralCode={referralCode}
              />
            )
          default:
            exhaustiveGuard(type)
        }
      })()}

      {onClose ? (
        <button
          className="btn btn-ghost w-full max-w-[309px] self-center"
          onClick={onClose}
        >
          <Trans>Cancel</Trans>
        </button>
      ) : (
        <p className="self-center text-grey4">
          <Trans>Already have an account?</Trans>&nbsp;
          <a href="/api/login" className="!text-signup-link underline">
            <Trans>Log in</Trans>
          </a>
        </p>
      )}

      <ErrorMessage error={signupPrivateError} />

      <ErrorMessage error={signupBusinessError} />

      <ModalDialog open={Boolean(pendingFederation)}>
        {pendingFederation && (
          <FederatedSignupConfirmation
            className="px-8 py-10"
            customerGroupCode={customerGroupCode}
            pendingFederation={pendingFederation}
            onSuccess={onSuccess}
            onCaptchaRequested={() => setEnableCaptcha(true)}
            onEmailVerification={setEmailVerification}
            onCancel={() => setPendingFederation(undefined)}
            country={country}
            language={language}
            ctaLabelText={ctaLabelText}
          />
        )}
      </ModalDialog>

      <AccountVerificationModal
        enableCaptcha={enableCaptcha}
        onCaptchaSolved={setCaptchaSolution}
        emailVerification={emailVerification}
        onSuccess={onSuccess}
        onFailure={() => {
          setAccountVerificationFailed(true)
          setEmailVerification(undefined)
          setEnableCaptcha(false)
          setCaptchaSolution(undefined)
        }}
      />

      <AppPromotionDrawer
        open={Boolean(downloadMobileAppLink)}
        downloadMobileAppLink={downloadMobileAppLink || ''}
        onLinkClick={() => {
          persistInstallationCode(signupResult?.installationCode)

          // We will be redirected and probably trigger an OS app switch leaving the browser frozen in the background.
          // Abort the login process first to ensure a decent app state
          setSignupResult(undefined)
          setAppPromotionDeclined(true)
          if (onClose) {
            onClose()
          }
        }}
        onClose={() => setAppPromotionDeclined(true)}
      />
    </div>
  )
}
